import React from 'react';

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface RadioGroupProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: RadioOption[];
  required?: boolean;
  error?: string;
  disabled?: boolean;
  helpText?: string;
  layout?: 'vertical' | 'horizontal';
}

const RadioGroup: React.FC<RadioGroupProps> = ({
  id,
  label,
  value,
  onChange,
  options,
  required = false,
  error,
  disabled = false,
  helpText,
  layout = 'vertical',
}) => {
  return (
    <div className="mb-4">
      <fieldset disabled={disabled}>
        <legend className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </legend>
        <div className={layout === 'horizontal' ? 'flex items-center space-x-6' : 'space-y-2'}>
          {options.map((option, index) => (
            <div key={option.value} className="flex items-center">
              <input
                type="radio"
                id={`${id}-${index}`}
                name={id}
                value={option.value}
                checked={value === option.value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled || option.disabled}
                required={required}
                className="h-4 w-4 text-primary border-gray-300 focus:ring-primary focus:ring-2"
              />
              <div className="ml-3">
                <label
                  htmlFor={`${id}-${index}`}
                  className={`block text-sm ${disabled || option.disabled ? 'text-gray-400' : 'text-gray-700'} cursor-pointer`}
                >
                  {option.label}
                </label>
                {option.description && (
                  <p className="text-sm text-gray-500 mt-1">
                    {option.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </fieldset>
      {helpText && !error && (
        <p className="mt-1 text-sm text-gray-500">
          {helpText}
        </p>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default RadioGroup;